<script lang="ts" setup>
import { ref, computed, onBeforeMount } from 'vue';
import { useAuth } from '@/plugins/auth';
import { useMedia } from '@/plugins/media';
import { useSignalR } from '@/plugins/signalr';
import { useStorage } from '@vueuse/core';
import { axios } from '@/plugins/axios';
import { useLogging } from '@/plugins/logging';
import { useFirebase } from '@/plugins/firebase';
import Account from '@/views/partials/layout/Account.vue';
import Notifications from '@/views/partials/layout/Notifications.vue';
import Breadcrumbs from '@/views/partials/navigation/Breadcrumbs.vue';
import Menu from '@/views/partials/navigation/MenuLogito.vue';
// import Menu from '@/views/partials/navigation/MenuClassic.vue';
import PushNotifications from "@/views/partials/layout/PushNotifications.vue";
import NotificationsService, { ChannelType } from '@/modules/core/common/services/NotificationsService';
// import Omnibox from "@/views/partials/layout/Omnibox.vue";
import { onClickOutside } from '@vueuse/core';
import { useThemeStore } from '@/store/theme';
import Search from "@/views/partials/layout/Search.vue";

defineOptions({
    name: 'default-layout'
});

const media = useMedia();
const { $auth } = useAuth();
const { $log } = useLogging();
const { channel } = useSignalR();
const { $firebase } = useFirebase();
const server = channel('authentication');
const channels = ref<ChannelType[]>([]);

const sidebar = ref(null);
const collapsed = useStorage('collapsed', true);
const hover = ref(false);
const termsInfoVisible = useStorage('termsInfoVisible', true);
const toggleTermsInfo = (): void =>
{
    termsInfoVisible.value = !termsInfoVisible.value;
};
const oneSignalEnabled = computed(() =>
{
    return !!(import.meta.env.VITE_APP_ONESIGNAL && import.meta.env.VITE_APP_ONESIGNAL.length);
});

onClickOutside(sidebar, () =>
{
    if (media.mobile())
    {
        collapsed.value = false;
    }
});

const themeStore = useThemeStore();
const changeTheme = (): void =>
{
    themeStore.changeTheme();
};

server.on.logout = async (sessionId: number): Promise<void> =>
{
    const session = Number($auth.claims()["sid"] || 0);

    if (sessionId == null || sessionId == session)
    {
        $auth.logout();
    }
};

const toggleMenu = (value?: boolean): void =>
{
    if (typeof value === 'boolean')
        collapsed.value = value;
    else
        collapsed.value = !collapsed.value;
};

const loadChannels = async (): Promise<void> =>
{
    try
    {
        channels.value = await NotificationsService.getChannels();
    }
    catch (ex)
    {
        channels.value = [];
        $log.debug(ex);
    }
};

const initFirebase = async (): Promise<void> =>
{
    try
    {
        const firebaseEnabled = channels.value.find(el => el.value === 'Firebase').enabled;

        if (firebaseEnabled)
        {
            // TODO: Uncomment after completing the backend
            // const token = await $firebase.init();
            // await axios.post('auth/firebase/token', { token });
        }
    }
    catch (ex)
    {
        $log.debug(ex);
    }
};

onBeforeMount(async (): Promise<void> =>
{
    await loadChannels();
    await initFirebase();
});
</script>

<template>
    <main id="layout" class="d-flex" :class="{'desktop': desktop, 'laptop': laptop, 'mobile': mobile, 'phone': phone, [mq]: true, 'collapsed': collapsed, 'hover': hover}">
        <portal-target name="all-apps-menu" />
        <portal-target name="more-apps-menu" />

        <nav id="sidebar" ref="sidebar" @mouseenter="hover = true" @mouseleave="hover = false">
            <Menu :collapsed="collapsed" :hover="hover || mobile" @toggle-menu="toggleMenu"></Menu>
        </nav>
        <section id="content">
            <header>
                <nav class="navbar bg-body align-items-stretch">
                    <button type="button" class="navbar-toggler toggle-menu" @click="collapsed = !collapsed">
                        <i class="fas fa-bars"></i>
                    </button>
                    <Search />
                    <p v-if="termsInfoVisible" class="terms-info">
                        <button type="button" class="terms-info-toggle" :title="$t('[[[Ukryj przypomnienie]]]')" :aria-label="$t('[[[Ukryj przypomnienie]]]')" @click="toggleTermsInfo">
                            <i class="far fa-eye-slash"></i>
                        </button>
                        {{ $t('[[[Przypominamy, że dostęp do aukcji jest aktywny tylko po akceptacji regulaminu. Jeśli już zaakceptowałeś - możesz licytować!]]]') }}
                        <a href="/static/Instrukcja_regulamin.pdf" target="_blank" class="terms-info-link" rel="noopener noreferrer">{{ $t('[[[Instrukcja (PDF)]]]') }}</a>
                    </p>
                    <button v-else type="button" class="terms-info-show" :title="$t('[[[Pokaż przypomnienie]]]')" :aria-label="$t('[[[Pokaż przypomnienie]]]')" @click="toggleTermsInfo">
                        <i class="far fa-eye"></i>
                    </button>
                    <button type="button" class="navbar-toggler me-1" @click="changeTheme">
                        <i class="far fa-fw fa-circle-half-stroke"></i>
                    </button>
                    <Notifications class="me-2" v-if="!$auth.impersonating()"></Notifications>
                    <Account class="me-3"></Account>
                </nav>
            </header>
            <section id="view" class="bg-body-tertiary position-relative">
                <Breadcrumbs></Breadcrumbs>
                <div class="scroll d-flex flex-column" data-name="wrapper">
                    <div class="wrapper flex-fill d-flex flex-column">
                        <router-view v-slot="{ Component }">
                            <transition name="fade" mode="out-in">
                                <component :is="Component" class="view-wrapper" />
                            </transition>
                        </router-view>
                    </div>
                </div>
            </section>
        </section>
    </main>
    <PushNotifications v-if="oneSignalEnabled" />
</template>

<style lang="scss">
#layout {
    --ideo-nav-width: var(--ideo-nav-width-default);
    --ideo-view-padding-size: 1rem;
    --ideo-view-outer-height: calc(100dvh - var(--ideo-header-height));
    --ideo-view-inner-height: calc(var(--ideo-view-outer-height) - var(--ideo-breadcrumbs-height));

    height: 100dvh;

    #sidebar {
        position: fixed;
        z-index: 20000;
        height: 100%;
        width: var(--ideo-nav-width);
        overflow: hidden;
        transition: var(--ideo-transition);
        background-color: var(--ideo-nav-bg);
        color: var(--ideo-nav-color);

        > header {
            height: var(--ideo-header-height);
            position: relative;
            z-index: 1;
        }
        > section {
            height: calc(100dvh - var(--ideo-header-height) - var(--ideo-footer-height));
        }
        > footer {
            height: var(--ideo-footer-height);
            width: var(--ideo-nav-width);
        }
    }
    #content {
        height: 100%;
        width: calc(100dvw - var(--ideo-nav-width));
        margin-left: var(--ideo-nav-width);
        transition: var(--ideo-transition);

        > header {
            height: var(--ideo-header-height);

            > .navbar {
                padding: 0px;
                border-bottom: 1px solid var(--bs-border-color);
                height: calc(var(--ideo-header-height) - 1px);
            }
        }
        > #view {
            overflow: hidden;
            height: var(--ideo-view-outer-height);

            > .scroll {
                height: calc(var(--ideo-view-inner-height));

                > .wrapper {
                    padding: var(--ideo-view-padding-size);
                }
            }
        }
    }

    .navbar-toggler {
        height: var(--ideo-header-height);
        padding: 0 14px;
        border: none;
        border-radius: 0;

        &:hover, &.active i {
            color: var(--bs-primary)
        }

        &.toggle-menu {
            padding: 0 16px 0 22px;
        }
    }

    .terms-info {
        margin: 0;
        padding: 0.75rem 1rem;
        background-color: #fff5f5;
        border: 2px solid #dc3545;
        border-radius: 0.375rem;
        color: #721c24;
        font-size: 0.875rem;
        font-weight: 500;
        line-height: 1.5;
        text-align: center;
        flex: 1;
        max-width: 100%;
        white-space: normal;
        word-wrap: break-word;

        @media (max-width: 768px) {
            font-size: 0.8125rem;
            padding: 0.625rem 0.875rem;
        }

        &-link {
            color: #dc3545;
            font-weight: 600;
            text-decoration: underline;
            margin-left: 0.5rem;
            transition: color 0.2s ease;

            &:hover {
                color: #b02a37;
                text-decoration: none;
            }

            &:focus {
                outline: 2px solid #dc3545;
                outline-offset: 2px;
                border-radius: 2px;
            }
        }

        &-toggle {
            float: right;
            background: transparent;
            border: none;
            color: #721c24;
            cursor: pointer;
            padding: 0 0 0 0.5rem;
            line-height: 1;
            opacity: 0.7;
            transition: opacity 0.2s ease;

            &:hover {
                opacity: 1;
            }

            &:focus {
                outline: 2px solid #dc3545;
                outline-offset: 2px;
                border-radius: 2px;
            }
        }
    }

    .terms-info-show {
        background: transparent;
        border: 1px solid #dc3545;
        color: #dc3545;
        cursor: pointer;
        padding: 0.5rem 0.625rem;
        border-radius: 0.375rem;
        line-height: 1;
        transition: background-color 0.2s ease, color 0.2s ease;

        &:hover {
            background-color: #dc3545;
            color: #fff;
        }

        &:focus {
            outline: 2px solid #dc3545;
            outline-offset: 2px;
        }
    }

    &.collapsed:not(.mobile) {
        --ideo-nav-width: var(--ideo-nav-width-collapsed);

        #sidebar {
            > section {
                height: calc(100dvh - var(--ideo-header-height));
            }
            > footer {
                display: none;
                width: var(--ideo-nav-width-default);
            }
        }
        #content {
            margin-left: var(--ideo-nav-width-collapsed);
        }

        &.hover {
            #sidebar {
                width: var(--ideo-nav-width-default);

                > section {
                    height: calc(100dvh - var(--ideo-header-height) - var(--ideo-footer-height));
                }
                > footer {
                    display: block;
                }
            }
        }
    }

    &.mobile {
        #sidebar {
           visibility: hidden;
            width: 0;
        }
        #content {
            width: 100%;
            margin-left: 0;
            display: flex;
            flex-direction: column;
        }

        #content > header {
            height: auto;
        }

        #content > #view {
            height: auto;
            flex: 1;
        }

        &.collapsed {
            #sidebar {
                visibility: visible;
                width: var(--ideo-nav-width-default);
            }
        }
    }
}
</style>
